import React from "react";
import { Box, Typography, Card, Checkbox, Stack } from "@mui/material";
import { Link } from "react-router-dom";
import { convertPenniesToCurrency } from "@tlc/ls2pac/components/AccountMenu";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { GridValidRowModel } from "@mui/x-data-grid";
import { useLocalizedShortDate } from "../../../../hooks/useLocalizedShortDate";

export interface FinesMobileCardsProps {
  rows: readonly GridValidRowModel[];
  selectedIds: number[];
  isStudent: boolean;
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
}

export const FinesMobileCards: React.FC<FinesMobileCardsProps> = ({
  rows,
  selectedIds,
  setSelectedIds,
  isStudent,
}) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { translate } = useLanguage();
  const formatDate = useLocalizedShortDate();

  const useCheckboxSelection =
    !isStudent && locationConfiguration?.pacSettings.ecommerceEnabled;

  const handleToggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((prevId) => prevId !== id)
        : [...prev, id],
    );
  };

  return (
    <Stack spacing={2} width="100%" pb={2}>
      {rows.map((row) => {
        const isSelected = selectedIds.includes(row.id as number);

        return (
          <Card
            key={row.id}
            component="article" // tells screen reader this is a distinct block of content
            tabIndex={0} // ensure the cards are discoverable by tab key and screen readers
            variant="outlined"
            sx={{
              p: 2,
              borderColor: isSelected ? "primary.main" : "divider",
              bgcolor: isSelected ? "primary.50" : "background.paper",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "2px",
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box flexGrow={1} minWidth={0} pr={useCheckboxSelection ? 1 : 0}>
              {/* Date & Fine Amount */}
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  <span className="sr-only">{translate("date")}: </span>
                  {formatDate(row.date)}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  <span className="sr-only">{translate("amount")}: </span>
                  {convertPenniesToCurrency(row.fine)}
                </Typography>
              </Box>

              {/* Title, Author, Fee Comments */}
              <Box display="flex" flexDirection="column">
                {row.resource && row.resource.id && row.resource.shortTitle ? (
                  <Link to={`/resource/${row.resource.id}`}>
                    <Typography component="span" variant="body1">
                      {`${row.resource.shortTitle ?? row.title} `}
                    </Typography>
                  </Link>
                ) : (
                  row.title && (
                    <Typography component="span" variant="body1">
                      {row.title}
                    </Typography>
                  )
                )}

                {(row.resource?.shortAuthor || row.author) && (
                  <Typography
                    component="h4"
                    variant="body2"
                    color="text.secondary"
                  >
                    {translate("by")} {row.resource?.shortAuthor || row.author}
                  </Typography>
                )}

                {row.feeComment && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {row.feeComment}
                  </Typography>
                )}
              </Box>
              </Box>

              {useCheckboxSelection && (
                <Box flexShrink={0} mt={-0.5} mr={-2}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleSelection(row.id as number)}
                    slotProps={{
                      input: {
                        "aria-label": `${translate("select")} ${
                          row.resource?.shortTitle ?? row.title
                        }`,
                      },
                    }}
                  />
                </Box>
              )}             
            </Box>
          </Card>
        );
      })}
    </Stack>
  );
};
